import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uiAssetsApi } from '@/lib/api/uiAssets';
import type { CreateUiAssetRequest, UpdateUiAssetRequest } from '@/types/uiAssets';

const QUERY_KEY = 'ui-assets';

export function useUIAssets(group?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, group],
    queryFn: () => uiAssetsApi.list(group),
  });
}

export function useUIAssetDetail(id: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => uiAssetsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateUIAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUiAssetRequest) => uiAssetsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateUIAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUiAssetRequest }) =>
      uiAssetsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUploadUIAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uiAssetsApi.upload(id, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useRollbackUIAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      uiAssetsApi.rollback(id, version),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useToggleRemote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      uiAssetsApi.toggleRemote(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useDeleteUIAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => uiAssetsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}
